import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import './TrainingSubmission.css';

function TrainingSubmission({ playerId }) {
  const [dogs, setDogs] = useState([]);
  const [staff, setStaff] = useState([]);
  const [selectedDog, setSelectedDog] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('');
  const [artworkUrl, setArtworkUrl] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [showResults, setShowResults] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!playerId) throw new Error('Please log in');

        // Fetch dogs
        console.log('Fetching dogs for playerId:', playerId);
        const dogsRes = await fetch(`${API_BASE_URL}/my-dogs`, {
          headers: { 'player-id': playerId }
        });
        if (!dogsRes.ok) {
          const errorText = await dogsRes.text();
          throw new Error(`Failed to fetch dogs: ${dogsRes.status} ${errorText}`);
        }
        const dogsData = await dogsRes.json();
        console.log('Dog IDs:', dogsData.map(dog => ({
          _id: dog._id,
          type: typeof dog._id,
          registeredName: dog.registeredName
        })));
        setDogs(dogsData.filter(dog => dog.status === 'Approved'));

        // Fetch staff
        console.log('Fetching staff for playerId:', playerId);
        const staffRes = await fetch(`${API_BASE_URL}/staff`, {
          headers: { 'player-id': playerId }
        });
        if (!staffRes.ok) {
          const errorText = await staffRes.text();
          throw new Error(`Failed to fetch staff: ${staffRes.status} ${errorText}`);
        }
        const staffData = await staffRes.json();
        console.log('Raw staff data:', JSON.stringify(staffData.map(s => ({
          staffName: s.staffName,
          dogReputation: s.dogReputation
        })), null, 2));
        setStaff(staffData);

        // Fetch training submissions
        console.log('Fetching training submissions for playerId:', playerId);
        const submissionsRes = await fetch(`${API_BASE_URL}/training-submissions`, {
          headers: { 'player-id': playerId }
        });
        if (!submissionsRes.ok) {
          const errorText = await submissionsRes.text();
          throw new Error(`Failed to fetch submissions: ${submissionsRes.status} ${errorText}`);
        }
        const submissionsData = await submissionsRes.json();
        console.log('Submissions data:', submissionsData);
        setSubmissions(submissionsData);

        setLoading(false);
      } catch (error) {
        setMessage('Error: ' + error.message);
        console.error('Fetch error:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [playerId]);

  useEffect(() => {
    if (!selectedDog) {
      setAvailableStaff([]);
      setSelectedStaff('');
      return;
    }

    console.log('Filtering staff for selectedDog:', selectedDog, 'Type:', typeof selectedDog);
    const assignedStaff = staff.filter(s => {
      if (!s.dogReputation || !Array.isArray(s.dogReputation)) {
        console.log(`Staff ${s.staffName} has invalid dogReputation:`, s.dogReputation);
        return false;
      }
      return s.dogReputation.some(r => {
        if (!r.dogId || !r.dogId._id) {
          console.log(`Invalid dogId in ${s.staffName}'s dogReputation:`, r);
          return false;
        }
        const dogId = String(r.dogId._id);
        console.log('Comparing dogId:', dogId, 'with selectedDog:', selectedDog, 'Types:', typeof dogId, typeof selectedDog);
        return dogId === selectedDog;
      });
    });
    console.log('Selected dog:', selectedDog, 'Assigned staff:', JSON.stringify(assignedStaff.map(s => s.staffName), null, 2));
    setAvailableStaff(assignedStaff);
    setSelectedStaff('');
  }, [selectedDog, staff]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');

    try {
      if (!selectedDog || !selectedStaff || !artworkUrl) {
        setMessage('Error: Please fill in all fields');
        return;
      }

      console.log('Submitting training:', { playerId, dogId: selectedDog, staffId: selectedStaff, artworkUrl });
      const response = await fetch(`${API_BASE_URL}/training-submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'player-id': playerId
        },
        body: JSON.stringify({
          playerId,
          dogId: selectedDog,
          staffId: selectedStaff,
          artworkUrl
        })
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server returned non-JSON response');
      }

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Submission failed');

      setMessage('Training submitted successfully!');
      console.log('Training submitted:', data);
      setSelectedDog('');
      setSelectedStaff('');
      setArtworkUrl('');
      const submissionsRes = await fetch(`${API_BASE_URL}/training-submissions`, {
        headers: { 'player-id': playerId }
      });
      const submissionsData = await submissionsRes.json();
      console.log('Refreshed submissions:', submissionsData);
      setSubmissions(submissionsData);
    } catch (error) {
      setMessage('Error: ' + error.message);
      console.error('Submission error:', error);
    }
  };

  const handleViewResults = async (trainingId) => {
    try {
      console.log('Fetching results for trainingId:', trainingId);
      const response = await fetch(`${API_BASE_URL}/training-results/${trainingId}`, {
        headers: { 'player-id': playerId }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch results: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      setShowResults(data);

      // Mark as viewed
      await fetch(`${API_BASE_URL}/training-viewed/${trainingId}`, {
        method: 'PATCH',
        headers: { 'player-id': playerId }
      });

      const submissionsRes = await fetch(`${API_BASE_URL}/training-submissions`, {
        headers: { 'player-id': playerId }
      });
      const submissionsData = await submissionsRes.json();
      console.log('Submissions after viewing:', submissionsData);
      setSubmissions(submissionsData);
    } catch (error) {
      setMessage('Error fetching results: ' + error.message);
      console.error('Results error:', error);
    }
  };

  const handleDismiss = async (submissionId) => {
    try {
      console.log('Dismissing training:', submissionId);
      const response = await fetch(`${API_BASE_URL}/dismiss-training/${submissionId}`, {
        method: 'POST',
        headers: { 'player-id': playerId }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to dismiss training: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      console.log('Dismiss response:', data);

      // Refresh submissions
      const submissionsRes = await fetch(`${API_BASE_URL}/training-submissions`, {
        headers: { 'player-id': playerId }
      });
      const submissionsData = await submissionsRes.json();
      console.log('Submissions after dismissal:', submissionsData);
      setSubmissions(submissionsData);
    } catch (error) {
      setMessage('Error dismissing training: ' + error.message);
      console.error('Dismiss error:', error);
    }
  };

  if (!playerId) return <div className="training-container"><p>Please log in to submit training.</p></div>;
  if (loading) return <div className="training-container"><p>Loading...</p></div>;

  const noStaffMessage = selectedDog && availableStaff.length === 0
    ? 'This dog isn’t assigned to a staff member.'
    : null;

  const pendingOrProcessedSubmissions = submissions.filter(s => {
    console.log('Checking submission:', s._id, 'Status:', s.status, 'Dismissed:', s.dismissed);
    return (
      s.status === 'Pending' ||
      (s.status === 'Approved' && !s.viewed) ||
      (s.status === 'Rejected' && !s.dismissed)
    );
  });

  console.log('Filtered submissions:', pendingOrProcessedSubmissions.map(s => ({
    _id: s._id,
    status: s.status,
    denyReason: s.denyReason || '-',
    dismissed: s.dismissed
  })));

  return (
    <div className="training-container">
      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}
      <h2>Training Submission</h2>

      {/* Submissions Table */}
      {pendingOrProcessedSubmissions.length > 0 && (
        <div className="submissions-table">
          <h3>Training Submissions</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Dog</th>
                <th>Staff</th>
                <th>Status</th>
                <th>Message</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pendingOrProcessedSubmissions.map(submission => (
                <tr key={submission._id}>
                  <td>{new Date(submission.submissionDate).toLocaleDateString()}</td>
                  <td>{submission.dogId?.registeredName || 'Unknown'}</td>
                  <td>{submission.staffId?.staffName || 'Unknown'}</td>
                  <td>{submission.status}</td>
                  <td>{submission.denyReason || '-'}</td>
                  <td>
                    {submission.status === 'Approved' && !submission.viewed && (
                      <button
                        onClick={() => handleViewResults(submission._id)}
                        className="results-button"
                      >
                        See Results
                      </button>
                    )}
                    {submission.status === 'Rejected' && !submission.dismissed && (
                      <button
                        onClick={() => handleDismiss(submission._id)}
                        className="dismiss-button"
                      >
                        Dismiss
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Training Form */}
      <form onSubmit={handleSubmit} className="training-form">
        <div className="form-group">
          <label htmlFor="dog">Select Dog:</label>
          <select
            id="dog"
            value={selectedDog}
            onChange={(e) => setSelectedDog(e.target.value)}
            className="select-input"
          >
            <option value="">-- Select a Dog --</option>
            {dogs.map(dog => (
              <option key={dog._id} value={dog._id.toString()}>
                {dog.registeredName} ({dog.breed})
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="staff">Select Staff:</label>
          <select
            id="staff"
            value={selectedStaff}
            onChange={(e) => setSelectedStaff(e.target.value)}
            className="select-input"
            disabled={noStaffMessage}
          >
            <option value="">-- Select Staff --</option>
            {availableStaff.map(s => (
              <option key={s._id} value={s._id}>
                {s.staffName}
              </option>
            ))}
          </select>
          {noStaffMessage && <p className="error">{noStaffMessage}</p>}
        </div>
        <div className="form-group">
          <label htmlFor="artworkUrl">Artwork URL:</label>
          <input
            type="url"
            id="artworkUrl"
            value={artworkUrl}
            onChange={(e) => setArtworkUrl(e.target.value)}
            placeholder="https://example.com/artwork.png"
            className="text-input"
            required
          />
        </div>
        <button
          type="submit"
          className="submit-button"
          disabled={!selectedDog || !selectedStaff || !artworkUrl || noStaffMessage}
        >
          Submit Training
        </button>
      </form>

      {showResults && (
        <div className="modal">
          <div className="modal-content">
            <h3>Training Results</h3>
            <p><strong>Interaction Points:</strong> {showResults.interactionPoints || 0}</p>
            <p>{showResults.flavorText || '--'}</p>
            {showResults.itemsAwarded?.length > 0 && (
              <div className="items-area">
                <h4>Items:</h4>
                {showResults.itemsAwarded.map((item, index) => (
                  <div key={index} className="item">
                    <p>{item.name}</p>
                    {item.artUrl && <img src={item.artUrl} alt={item.name} className="item-img" />}
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setShowResults(null)} className="close-button">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrainingSubmission;